import { Fragment, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import {
  useMovieStore,
  useSelectionStore,
  useEditorStore,
  useTimelineStore,
  useAuthStore,
} from '../../stores';
import type {
  SceneObject,
  LibraryItem,
  KeyframeState,
  LibraryObjectResponse,
  SceneObjectResponse,
  SceneResponse,
} from '../../types';
import { useUIStore } from '../../stores/uiStore';
import { useSignalR } from '../../hooks';
import { useSceneEffectsLoader } from '../../hooks/useSceneEffectsSync';
import { useStageRuntime } from './stageRuntime';
import { useSceneEffectsRenderer } from './sceneEffects/useSceneEffectsRenderer';
import { importVectorPackageFile, resolveAssetUrl, type LibraryObjectUploadResponse } from '../../util/api';
import { resolveShapeFieldsFromBody, serializeShapeBody, shapeBodyFromSceneObject } from '../../util/shapeBody';
import { resolveTextFieldsFromBody, serializeTextBody, textBodyFromSceneObject } from '../../util/textBody';
import {
  extractAssetPathFromBody,
  resolveImageFieldsFromBody,
} from '../../util/imageBody';
import { resolveStateTarget } from '../../util/resolveStateTarget';
import {
  sceneObjectDragPositionPatchJson,
  sceneObjectToLayoutPropertiesJson,
  sceneObjectTransformPatchJson,
} from '../../util/sceneObjectLayoutProperties';
import { sortTimelineLayers } from '../../util/timeline';
import {
  buildMarqueeRect,
  collectIdsInMarquee,
  marqueeModeFromDrag,
  type MarqueeMode,
  type MarqueeRect,
} from '../../util/marqueeSelection';
import {
  buildChildrenByParent,
  buildObjectsById,
  collectDescendantIds,
  getWorldAxisAlignedBounds,
  getWorldBounds,
  getWorldPose,
  getGroupParent,
  isGroupContainer,
  reparentLayoutPatch,
} from '../../util/sceneObjectHierarchy';
import {
  compensateTopLeftForPivotChange,
  formatPivot,
  parsePivot,
  pivotOffsetPx,
} from '../../util/pivot';
import { StageLayer } from './StageLayer';
import { StageRulerFrame } from './StageRulerFrame';
import { SceneObjectComponent } from './SceneObject';
import { SceneObjectQuickMenu, type SceneObjectQuickMenuActions } from './SceneObjectQuickMenu';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useTranslation, type MessageKey } from '../../i18n';
import { generateCharacter, type CharacterPresetId } from '../../util/characterPresets';
import { ContextMenu } from '@radix-ui/themes';
import { isPlacementOrDrawingTool, useStageDrawing } from './drawing';
import './Stage.css';

/** Screen-space threshold before empty-area drag becomes a marquee (click stays deselect). */
const MARQUEE_DRAG_THRESHOLD_PX = 4;

interface DragState {
  isDragging: boolean;
  objectId: string | null;
  startX: number;
  startY: number;
  objectStartX: number;
  objectStartY: number;
}

interface ResizeState {
  isResizing: boolean;
  objectId: string | null;
  handle: ResizeHandle | null;
  startX: number;
  startY: number;
  objectStartX: number;
  objectStartY: number;
  objectStartWidth: number;
  objectStartHeight: number;
}

interface RotateState {
  isRotating: boolean;
  objectId: string | null;
  startPointerAngle: number;
  objectStartRotation: number;
  centerX: number;
  centerY: number;
}

interface PivotDragState {
  isDragging: boolean;
  objectId: string | null;
  startClientX: number;
  startClientY: number;
  startOx: number;
  startOy: number;
  startX: number;
  startY: number;
  width: number;
  height: number;
  rotation: number;
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
type RotateHandle = 'nw' | 'ne' | 'se' | 'sw';

function normalizeRotationDegrees(degrees: number): number {
  const wrapped = ((degrees + 180) % 360 + 360) % 360 - 180;
  return Math.round(wrapped);
}

export function Stage() {
  const { t } = useTranslation();
  const stageRef = useRef<HTMLDivElement>(null);
  /** Persistent Stage WebGL canvas — effects and future 3D objects share this context. */
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const keepAspectRatio = useUIStore((s) => s.keepAspectRatio);
  const stageResizeEnabled = useUIStore((s) => s.stageResizeEnabled);
  const stageRotateEnabled = useUIStore((s) => s.stageRotateEnabled);
  const {
    currentMovie,
    currentScene,
    currentLayer,
    setCurrentLayer,
    updateSceneObject,
    addSceneObject,
    addLibraryItem,
    deleteSceneObject,
    batchUpdateSceneObjects,
  } = useMovieStore();
  const {
    selectedIds,
    focusedId,
    select,
    multiSelect,
    clearSelection,
    addToSelection,
    setHovered,
    hoveredId,
  } = useSelectionStore();
  const {
    zoom,
    currentTool,
    setTool,
    snapToGrid,
    gridSize,
    showGrid,
    fillColor,
    strokeColor,
    strokeWidth,
    strokeStyle,
    isPlaying,
    sceneEffects,
    pendingCharacterInsert,
    clearCharacterInsert,
  } = useEditorStore();
  useSceneEffectsLoader();
  // Subscribe only to layers to avoid re-rendering Stage on every timeline state change.
  const accessToken = useAuthStore((state) => state.accessToken);
  const layers = useTimelineStore((state) => state.layers);
  const clearTimelineSelection = useTimelineStore((state) => state.clearTimelineSelection);
  const {
    status: signalRStatus,
    connect,
    sceneObjectSave,
    sceneObjectsGet,
    sceneObjectsSave,
    sceneObjectCreateFromLibraryItem,
    sceneObjectDeleteBatch,
    sceneObjectPatchStateProperties,
    sceneObjectUpdateHierarchy,
    sceneObjectUpdatePivot,
    sceneObjectSetVisibility,
    sceneObjectSetLock,
    sceneObjectsResort,
  } = useSignalR();

  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    objectId: null,
    startX: 0,
    startY: 0,
    objectStartX: 0,
    objectStartY: 0,
  });

  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    objectId: null,
    handle: null,
    startX: 0,
    startY: 0,
    objectStartX: 0,
    objectStartY: 0,
    objectStartWidth: 0,
    objectStartHeight: 0,
  });

  const [rotateState, setRotateState] = useState<RotateState>({
    isRotating: false,
    objectId: null,
    startPointerAngle: 0,
    objectStartRotation: 0,
    centerX: 0,
    centerY: 0,
  });

  const [pivotDragState, setPivotDragState] = useState<PivotDragState>({
    isDragging: false,
    objectId: null,
    startClientX: 0,
    startClientY: 0,
    startOx: 0.5,
    startOy: 0.5,
    startX: 0,
    startY: 0,
    width: 0,
    height: 0,
    rotation: 0,
  });

  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [layerSelectionWarningOpen, setLayerSelectionWarningOpen] = useState(false);
  const [isLibraryItemDragOver, setIsLibraryItemDragOver] = useState(false);
  const [marqueeVisual, setMarqueeVisual] = useState<{
    rect: MarqueeRect;
    mode: MarqueeMode;
  } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPanningRef = useRef(false);
  const panStartState = useRef<{ scrollX: number; scrollY: number; clientX: number; clientY: number } | null>(null);
  const panListenersRef = useRef<{
    move: (e: PointerEvent | MouseEvent) => void;
    up: () => void;
  } | null>(null);
  const marqueeSessionRef = useRef<{
    startClientX: number;
    startClientY: number;
    startStageX: number;
    startStageY: number;
    additive: boolean;
    activated: boolean;
  } | null>(null);
  const marqueeListenersRef = useRef<{
    move: (e: PointerEvent) => void;
    up: (e: PointerEvent) => void;
  } | null>(null);

  const stageWidth = currentMovie?.width ?? 800;
  const stageHeight = currentMovie?.height ?? 600;
  const stageRuntime = useStageRuntime(canvasRef, stageWidth, stageHeight);
  useSceneEffectsRenderer(stageRuntime, stageWidth, stageHeight, sceneEffects);

  const resolveTargetLayerId = useCallback((): number | null => {
    const fromSelection = currentLayer ? Number(currentLayer.id) : NaN;
    if (!Number.isNaN(fromSelection) && fromSelection > 0) {
      return fromSelection;
    }
    return null;
  }, [currentLayer]);

  const normalizeObjectSize = useCallback((value: number | undefined): number => {
    return typeof value === 'number' && value > 0 ? Math.round(value) : 100;
  }, []);

  const mapSceneObjectResponseToStore = useCallback((response: SceneObjectResponse): SceneObject => {
    let props: Record<string, number> = {};
    if (response.defaultState?.properties) {
      try {
        props = JSON.parse(response.defaultState.properties);
      } catch {
        // ignore invalid json
      }
    }

    const rawSourcePath =
      response.url ?? response.fullPath ?? extractAssetPathFromBody(response.body);
    const resolvedSrc = rawSourcePath ? resolveAssetUrl(rawSourcePath) : '';

    const shapeFields = resolveShapeFieldsFromBody(response.type, response.body);
    const textFields = resolveTextFieldsFromBody(response.type, response.body, response.name);
    const imageFields = resolveImageFieldsFromBody(response.type, response.body);
    const {
      shapeType,
      pathData,
      fill,
      fillMode: shapeFillMode,
      fillGradient: shapeFillGradient,
      stroke,
      strokeWidth,
      strokeStyle,
      strokeSides,
      shadow: shapeShadow,
      glow: shapeGlow,
    } = shapeFields;
    const { fontSize, textAlign } = textFields;
    const textFill = textFields.fill;
    const resolvedFill = response.type === 'text' ? textFill : fill;
    const resolvedName = response.type === 'text' ? (textFields.name ?? response.name) : response.name;
    const resolvedFillMode =
      response.type === 'text' ? textFields.fillMode : response.type === 'shape' ? shapeFillMode : undefined;
    const resolvedFillGradient =
      response.type === 'text'
        ? textFields.fillGradient
        : response.type === 'shape'
          ? shapeFillGradient
          : undefined;
    const resolvedShadow =
      response.type === 'image'
        ? imageFields.shadow
        : response.type === 'text'
          ? textFields.shadow
          : shapeShadow;
    const resolvedGlow =
      response.type === 'image'
        ? imageFields.glow
        : response.type === 'text'
          ? textFields.glow
          : shapeGlow;

    const x = Math.round(props.left ?? props.x ?? response.dropPosition?.left ?? 0);
    const y = Math.round(props.top ?? props.y ?? response.dropPosition?.top ?? 0);
    const width = normalizeObjectSize(props.width ?? response.width);
    const height = normalizeObjectSize(props.height ?? response.height);
    const rotation = props.rotation ?? 0;
    const opacity = props.opacity ?? 1;

    return {
      id: String(response.id),
      type: (response.type as SceneObject['type']) || 'image',
      name: resolvedName,
      src: resolvedSrc || undefined,
      libraryObjectId: response.libraryObjectId ?? undefined,
      parentObjectId: response.parentObjectId != null ? String(response.parentObjectId) : null,
      isGroup: response.isGroup === true || response.type === 'group',
      x,
      y,
      width,
      height,
      rotation,
      opacity,
      zIndex: response.sorter ?? currentScene?.objects.length ?? 0,
      visible: response.isVisible !== false,
      locked: response.isLocked === true,
      pivotPoint: response.pivotPoint?.trim() ? response.pivotPoint : 'center',
      layerId: response.layerId,
      shapeType,
      pathData,
      fill: resolvedFill,
      fillMode: resolvedFillMode,
      fillGradient: resolvedFillGradient,
      stroke,
      strokeWidth,
      strokeStyle,
      strokeSides,
      shadow: resolvedShadow,
      glow: resolvedGlow,
      fontSize,
      textAlign,
      defaultPose: { x, y, width, height, rotation, opacity },
      states: [],
    };
  }, [currentScene?.objects.length, normalizeObjectSize]);

  const persistShapeObject = useCallback(async (newObject: SceneObject) => {
    if (!currentMovie || !currentScene) return null;

    const targetLayerId = resolveTargetLayerId();
    if (!targetLayerId) {
      setLayerSelectionWarningOpen(true);
      return null;
    }

    if (signalRStatus !== 'Connected') {
      await connect();
    }

    const body =
      newObject.type === 'shape'
        ? serializeShapeBody(shapeBodyFromSceneObject(newObject))
        : newObject.type === 'text'
          ? serializeTextBody(textBodyFromSceneObject(newObject))
          : '';

    const payload: SceneObjectResponse = {
      id: 0,
      movieId: Number(currentMovie.id),
      sceneId: Number(currentScene.id),
      layerId: targetLayerId,
      libraryObjectId: null,
      name: newObject.name,
      description: '',
      type: newObject.type,
      body,
      fileName: undefined,
      fullPath: '',
      isVisible: true,
      isLocked: false,
      width: newObject.width,
      height: newObject.height,
      dropPosition: { top: newObject.y, left: newObject.x },
      defaultState: {
        id: 0,
        keyFrameId: 0,
        sceneObjectId: 0,
        properties: sceneObjectToLayoutPropertiesJson(newObject),
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const created = await sceneObjectSave(payload);
    return mapSceneObjectResponseToStore(created);
  }, [currentMovie, currentScene, resolveTargetLayerId, signalRStatus, connect, sceneObjectSave, mapSceneObjectResponseToStore]);

  const persistFromLibraryItem = useCallback(async (item: LibraryItem, x: number, y: number) => {
    if (!currentMovie || !currentScene) return null;

    const targetLayerId = resolveTargetLayerId();
    if (!targetLayerId) {
      setLayerSelectionWarningOpen(true);
      return null;
    }

    if (signalRStatus !== 'Connected') {
      await connect();
    }

    const libraryObjectId = Number(item.id);
    if (!Number.isFinite(libraryObjectId)) {
      console.warn('[Stage] Invalid library object id for persisted create', item.id);
      return null;
    }

    const libraryPayload: LibraryObjectResponse = {
      id: libraryObjectId,
      movieId: Number(currentMovie.id),
      name: item.name,
      description: '',
      type: item.type,
      fileName: '',
      width: 100,
      height: 100,
      dropPosition: { top: y, left: x },
    };

    const created = await sceneObjectCreateFromLibraryItem(
      libraryPayload,
      Number(currentScene.id),
      targetLayerId
    );

    return mapSceneObjectResponseToStore(created);
  }, [currentMovie, currentScene, resolveTargetLayerId, signalRStatus, connect, sceneObjectCreateFromLibraryItem, mapSceneObjectResponseToStore]);

  /**
   * Preset karakteri sahne ortasına bir group + shape child'lar olarak yazar.
   * Parçalar bugünkü shape nesneleridir; grup seçilince bütün, parça seçilince tekil düzenlenir.
   */
  const insertCharacter = useCallback(
    async (preset: CharacterPresetId, seed: number) => {
      if (!currentMovie || !currentScene) return;

      const targetLayerId = resolveTargetLayerId();
      if (!targetLayerId) {
        setLayerSelectionWarningOpen(true);
        return;
      }

      if (signalRStatus !== 'Connected') {
        await connect();
      }

      const character = generateCharacter(preset, seed, Math.round(stageHeight * 0.45));
      const groupX = Math.round((stageWidth - character.width) / 2);
      const groupY = Math.round((stageHeight - character.height) / 2);
      const maxZ = currentScene.objects.reduce((max, item) => Math.max(max, item.zIndex), -1);
      const now = new Date().toISOString();
      const movieId = Number(currentMovie.id);
      const sceneId = Number(currentScene.id);
      const groupName = t(`toolbar.characterPresets.${preset}` as MessageKey);

      const createdGroup = await sceneObjectSave({
        id: 0,
        movieId,
        sceneId,
        layerId: targetLayerId,
        parentObjectId: null,
        libraryObjectId: null,
        pivotPoint: 'center',
        name: groupName,
        description: '',
        type: 'group',
        body: '',
        fullPath: '',
        isVisible: true,
        isLocked: false,
        isGroup: true,
        width: character.width,
        height: character.height,
        dropPosition: { top: groupY, left: groupX },
        createdAt: now,
        updatedAt: now,
      });

      const groupObj = mapSceneObjectResponseToStore(createdGroup);
      groupObj.isGroup = true;
      groupObj.zIndex = maxZ + 1;
      groupObj.x = groupX;
      groupObj.y = groupY;
      groupObj.width = character.width;
      groupObj.height = character.height;
      groupObj.rotation = 0;
      groupObj.defaultPose = {
        x: groupX,
        y: groupY,
        width: character.width,
        height: character.height,
        rotation: 0,
        opacity: 1,
      };
      addSceneObject(groupObj);

      const children = await Promise.all(
        character.parts.map(async (part) => {
          const partObj: SceneObject = {
            id: `pending-${part.role}`,
            type: 'shape',
            name: part.role,
            parentObjectId: groupObj.id,
            x: part.x,
            y: part.y,
            width: part.width,
            height: part.height,
            rotation: part.rotation,
            opacity: 1,
            zIndex: part.zIndex,
            visible: true,
            locked: false,
            pivotPoint: part.pivotPoint,
            shapeType: part.shapeType,
            pathData: part.pathData,
            fill: part.fill,
            fillMode: part.fillMode,
            stroke: part.stroke,
            strokeWidth: part.strokeWidth,
            strokeStyle: part.strokeStyle,
          };

          const created = await sceneObjectSave({
            id: 0,
            movieId,
            sceneId,
            layerId: targetLayerId,
            parentObjectId: createdGroup.id,
            libraryObjectId: null,
            pivotPoint: part.pivotPoint,
            name: part.role,
            description: '',
            type: 'shape',
            body: serializeShapeBody(shapeBodyFromSceneObject(partObj)),
            fullPath: '',
            isVisible: true,
            isLocked: false,
            width: part.width,
            height: part.height,
            dropPosition: { top: part.y, left: part.x },
            defaultState: {
              id: 0,
              keyFrameId: 0,
              sceneObjectId: 0,
              properties: sceneObjectToLayoutPropertiesJson(partObj),
            },
            createdAt: now,
            updatedAt: now,
          });

          const childObj = mapSceneObjectResponseToStore(created);
          childObj.parentObjectId = groupObj.id;
          childObj.zIndex = part.zIndex;
          childObj.x = part.x;
          childObj.y = part.y;
          childObj.rotation = part.rotation;
          childObj.defaultPose = {
            x: part.x,
            y: part.y,
            width: childObj.width,
            height: childObj.height,
            rotation: part.rotation,
            opacity: 1,
          };
          return childObj;
        })
      );

      for (const child of children) addSceneObject(child);

      const resortItems = [
        { id: createdGroup.id, sorter: groupObj.zIndex },
        ...children
          .map((child) => ({ id: Number(child.id), sorter: child.zIndex }))
          .filter((item) => Number.isFinite(item.id)),
      ];
      await sceneObjectsResort({ items: resortItems });

      // Sunucu create sırasında rotation yazmaz; dönük parçalar için state'i tamamla.
      for (const child of children) {
        if (child.rotation === 0) continue;
        const idNum = Number(child.id);
        if (!Number.isFinite(idNum)) continue;
        await sceneObjectPatchStateProperties({
          sceneObjectId: idNum,
          keyFrameId: 0,
          patch: sceneObjectTransformPatchJson(child),
        });
      }

      select(groupObj.id);
    },
    [
      currentMovie,
      currentScene,
      resolveTargetLayerId,
      signalRStatus,
      connect,
      stageWidth,
      stageHeight,
      t,
      sceneObjectSave,
      mapSceneObjectResponseToStore,
      addSceneObject,
      sceneObjectsResort,
      sceneObjectPatchStateProperties,
      select,
    ]
  );

  // Toolbar'dan gelen karakter isteği; nonce ile StrictMode çift çalışmasına karşı korunur.
  const handledCharacterNonceRef = useRef(0);
  useEffect(() => {
    if (!pendingCharacterInsert) return;
    if (pendingCharacterInsert.nonce === handledCharacterNonceRef.current) return;
    handledCharacterNonceRef.current = pendingCharacterInsert.nonce;
    const { preset, seed } = pendingCharacterInsert;
    clearCharacterInsert();
    void insertCharacter(preset, seed).catch((error) => {
      console.error('[Stage] insertCharacter failed:', error);
    });
  }, [pendingCharacterInsert, clearCharacterInsert, insertCharacter]);

  const parseDroppedVectorFile = (e: React.DragEvent) => {
    const raw =
      e.dataTransfer.getData('application/x-prototype-vector-file') ||
      e.dataTransfer.getData('application/json');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as {
        type?: string;
        packageId?: string;
        fileId?: string;
        name?: string;
        src?: string;
        width?: number;
        height?: number;
      };
      if (parsed?.type !== 'vectorPackageFile') return null;
      if (!parsed.packageId || !parsed.fileId) return null;
      return parsed;
    } catch {
      return null;
    }
  };

  const parseDroppedLibraryItem = (e: React.DragEvent): LibraryItem | null => {
    const customPayload = e.dataTransfer.getData('application/x-prototype-library-item');
    const legacyPayload = e.dataTransfer.getData('application/json');
    const payload = customPayload || legacyPayload;
    if (!payload) return null;

    try {
      const parsed = JSON.parse(payload) as { type?: string; item?: LibraryItem };
      if (parsed?.type !== 'libraryItem' || !parsed.item) {
        return null;
      }
      return parsed.item;
    } catch {
      return null;
    }
  };

  const hasLibraryItemDragPayload = (e: React.DragEvent): boolean => {
    const types = Array.from(e.dataTransfer.types ?? []);
    return (
      types.includes('application/x-prototype-library-item') ||
      types.includes('application/x-prototype-vector-file') ||
      types.includes('application/json')
    );
  };

  // Pixel layout: her zaman tam sayı; snap açıksa grid'e hizala
  const snapValue = useCallback((value: number) => {
    if (!snapToGrid) return Math.round(value);
    return Math.round(value / gridSize) * gridSize;
  }, [snapToGrid, gridSize]);

  useEffect(() => {
    if (currentTool !== 'select') setHovered(null);
  }, [currentTool, setHovered]);

  useEffect(() => {
    if (!currentScene || selectedIds.length === 0) return;

    const activeObjectId = focusedId ?? selectedIds[0];
    if (!activeObjectId) return;

    const activeObject = currentScene.objects.find((obj) => obj.id === activeObjectId);
    if (!activeObject?.layerId) return;

    const targetLayer = layers.find(
      (layer) => layer.id === String(activeObject.layerId) || Number(layer.id) === activeObject.layerId
    );
    if (!targetLayer) return;
    if (currentLayer?.id === targetLayer.id) return;

    setCurrentLayer(targetLayer);
  }, [currentScene, selectedIds, focusedId, layers, currentLayer?.id, setCurrentLayer]);

  // Check if object's layer is locked
  const isLayerLocked = useCallback((layerId?: number) => {
    if (layerId == null) return false;
    const layer = layers.find(l => l.id === String(layerId));
    return layer?.locked ?? false;
  }, [layers]);

  // Check if object's layer is visible
  const isLayerVisible = useCallback((layerId?: number) => {
    if (layerId == null) return true;
    const layer = layers.find(l => l.id === String(layerId));
    return layer?.visible ?? true;
  }, [layers]);

  // Get stage coordinates from mouse event
  const getStageCoordinates = useCallback((e: { clientX: number; clientY: number }) => {
    if (!stageRef.current) return null;
    const rect = stageRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom,
    };
  }, [zoom]);

  const isPointerInStageBounds = useCallback((e: { clientX: number; clientY: number }) => {
    if (!stageRef.current) return false;
    const r = stageRef.current.getBoundingClientRect();
    return (
      e.clientX >= r.left &&
      e.clientX <= r.right &&
      e.clientY >= r.top &&
      e.clientY <= r.bottom
    );
  }, []);

  const isRulerChromeTarget = useCallback((el: Element) => {
    return Boolean(el.closest('.stage-ruler-cell, .stage-ruler-corner'));
  }, []);

  /** Stage canvas veya cetvel çevresindeki workspace padding — çizim/yerleştirme için. */
  const isPointerInDrawingWorkspace = useCallback(
    (e: { clientX: number; clientY: number }, target: EventTarget | null) => {
      if (!(target instanceof Element) || !wrapperRef.current?.contains(target)) return false;
      if (isRulerChromeTarget(target)) return false;
      if (target.closest('.stage, .stage-ruler-center, .stage-ruler-center-inner')) return true;
      return isPointerInStageBounds(e);
    },
    [isPointerInStageBounds, isRulerChromeTarget]
  );

  const {
    isShapeDrawingToolSelected,
    handleDrawingMouseDown,
    drawingPreview,
  } = useStageDrawing({
    currentTool,
    getStageCoordinates,
    isPointerInDrawingWorkspace,
    snapValue,
    fillColor,
    strokeColor,
    strokeWidth,
    strokeStyle,
    objectCount: currentScene?.objects.length ?? 0,
    persistShapeObject,
    addSceneObject,
    select,
  });

  const isInteractiveStageTarget = useCallback((el: Element) => {
    return Boolean(
      el.closest(
        '.stage-object, .resize-handle, .rotate-handle, .scene-object-quick-menu, .stage-library-drop-zone.active, button, a, input, textarea, select, [role="menu"], [role="menuitem"], [role="toolbar"]'
      )
    );
  }, []);

  const endMarqueeListeners = useCallback(() => {
    const listeners = marqueeListenersRef.current;
    if (listeners) {
      window.removeEventListener('pointermove', listeners.move);
      window.removeEventListener('pointerup', listeners.up);
      window.removeEventListener('pointercancel', listeners.up);
      marqueeListenersRef.current = null;
    }
    marqueeSessionRef.current = null;
    setMarqueeVisual(null);
  }, []);

  const applyMarqueeSelection = useCallback(
    (rect: MarqueeRect, mode: MarqueeMode, additive: boolean) => {
      const objects = currentScene?.objects ?? [];
      const objectsById = buildObjectsById(objects);
      const worldObjects = objects.map((obj) => ({
        id: obj.id,
        visible: obj.visible,
        locked: obj.locked,
        layerId: obj.layerId,
        ...getWorldBounds(obj, objectsById),
      }));
      const hitIds = collectIdsInMarquee(worldObjects, rect, mode, (obj) => {
        if (!obj.visible || obj.locked) return false;
        if (!isLayerVisible(obj.layerId) || isLayerLocked(obj.layerId)) return false;
        return true;
      });

      if (additive) {
        const merged = new Set([...useSelectionStore.getState().selectedIds, ...hitIds]);
        multiSelect([...merged]);
      } else {
        multiSelect(hitIds);
      }
    },
    [currentScene?.objects, isLayerVisible, isLayerLocked, multiSelect]
  );

  // Boş sahne drag → SolidWorks tarzı marquee; eşik altı tıklama → deselect.
  const handleStageBackgroundPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0 || currentTool !== 'select') return;

      const target = e.target;
      if (!(target instanceof Element) || !wrapperRef.current?.contains(target)) return;
      if (isInteractiveStageTarget(target) || isRulerChromeTarget(target)) return;

      const onStageCanvas = isPointerInStageBounds(e);
      const onWorkspacePadding = Boolean(
        target.closest('.stage-ruler-center, .stage-ruler-center-inner')
      );
      if (!onStageCanvas && !onWorkspacePadding) return;

      const coords = getStageCoordinates(e);
      if (!coords) return;

      e.preventDefault();
      setEditingTextId(null);
      endMarqueeListeners();

      marqueeSessionRef.current = {
        startClientX: e.clientX,
        startClientY: e.clientY,
        startStageX: coords.x,
        startStageY: coords.y,
        additive: e.shiftKey,
        activated: false,
      };

      const handleMove = (ev: PointerEvent) => {
        const session = marqueeSessionRef.current;
        if (!session) return;

        const dx = ev.clientX - session.startClientX;
        const dy = ev.clientY - session.startClientY;
        if (
          !session.activated &&
          dx * dx + dy * dy < MARQUEE_DRAG_THRESHOLD_PX * MARQUEE_DRAG_THRESHOLD_PX
        ) {
          return;
        }

        const current = getStageCoordinates(ev);
        if (!current) return;

        session.activated = true;
        const mode = marqueeModeFromDrag(session.startStageX, current.x);
        const rect = buildMarqueeRect(
          session.startStageX,
          session.startStageY,
          current.x,
          current.y
        );
        setMarqueeVisual({ rect, mode });
      };

      const handleUp = (ev: PointerEvent) => {
        const session = marqueeSessionRef.current;
        const visual = (() => {
          if (!session?.activated) return null;
          const current = getStageCoordinates(ev);
          if (!current) return null;
          return {
            mode: marqueeModeFromDrag(session.startStageX, current.x),
            rect: buildMarqueeRect(
              session.startStageX,
              session.startStageY,
              current.x,
              current.y
            ),
            additive: session.additive || ev.shiftKey,
          };
        })();

        const wasActivated = session?.activated ?? false;
        const additiveClick = session?.additive || ev.shiftKey;
        endMarqueeListeners();

        if (!wasActivated) {
          if (!additiveClick) clearSelection();
          return;
        }

        if (visual) {
          applyMarqueeSelection(visual.rect, visual.mode, visual.additive);
        }
      };

      marqueeListenersRef.current = { move: handleMove, up: handleUp };
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [
      currentTool,
      isInteractiveStageTarget,
      isRulerChromeTarget,
      isPointerInStageBounds,
      getStageCoordinates,
      endMarqueeListeners,
      clearSelection,
      applyMarqueeSelection,
    ]
  );

  useEffect(() => {
    if (currentTool !== 'select') endMarqueeListeners();
  }, [currentTool, endMarqueeListeners]);

  useEffect(() => () => endMarqueeListeners(), [endMarqueeListeners]);

  /** Yeni çizim/yerleştirme: properties paneli object tab + boş selection’a dönsün. */
  const beginNewObjectPlacement = useCallback(() => {
    clearSelection();
    clearTimelineSelection();
  }, [clearSelection, clearTimelineSelection]);

  // Handle stage mouse down for drawing
  const handleStageDrawingMouseDown = (e: React.PointerEvent) => {
    if (!isPlacementOrDrawingTool(currentTool)) return;
    if (!isPointerInDrawingWorkspace(e, e.target)) return;

    beginNewObjectPlacement();

    // Text tool: place a new text box at click (top-left), persist, then edit
    if (currentTool === 'text') {
      e.preventDefault();
      const coords = getStageCoordinates(e);
      if (!coords) return;
      const defaultWidth = 200;
      const defaultHeight = 56;
      const newObject: SceneObject = {
        id: `pending-text-${Date.now()}`,
        type: 'text',
        name: 'Text',
        x: snapValue(coords.x),
        y: snapValue(coords.y),
        width: defaultWidth,
        height: defaultHeight,
        rotation: 0,
        opacity: 1,
        zIndex: currentScene?.objects.length ?? 0,
        visible: true,
        locked: false,
        pivotPoint: 'center',
        fill: fillColor,
        fontSize: 16,
        textAlign: 'center',
      };
      void persistShapeObject(newObject)
        .then((createdObject) => {
          if (!createdObject) return;
          addSceneObject(createdObject);
          select(createdObject.id);
          setEditingTextId(createdObject.id);
          setTool('select');
        })
        .catch((error) => {
          console.error('[Stage] Failed to persist text scene object:', error);
        });
      return;
    }

    handleDrawingMouseDown(e);
  };

  const isDragAllowedForTool = useCallback(() => {
    if (currentTool === 'pan') return false;
    return !isShapeDrawingToolSelected;
  }, [currentTool, isShapeDrawingToolSelected]);

  type LayoutCommitKind = 'drag' | 'resize' | 'rotate';

  /** Sürükleme / resize / rotate bittiğinde: resolveStateTarget → keyframe state veya defaultPose + jsonb patch. */
  const commitLayoutAfterTransform = useCallback(
    async (objectId: string, kind: LayoutCommitKind) => {
      const movie = useMovieStore.getState();
      const timeline = useTimelineStore.getState();
      const editor = useEditorStore.getState();
      const obj = movie.currentScene?.objects.find((o) => o.id === objectId);
      if (!obj) return;

      const layerIdFromObj = obj.layerId;
      const layerIdFromCurrent = movie.currentLayer ? Number(movie.currentLayer.id) : NaN;
      const layerIdNum =
        layerIdFromObj != null && !Number.isNaN(Number(layerIdFromObj))
          ? Number(layerIdFromObj)
          : !Number.isNaN(layerIdFromCurrent)
            ? layerIdFromCurrent
            : NaN;
      if (Number.isNaN(layerIdNum)) return;

      const layerIdStr = String(layerIdNum);
      const layerKeyframes = timeline.keyframes[layerIdStr] ?? [];
      const target = resolveStateTarget({
        layerId: layerIdStr,
        currentFrame: editor.currentFrame,
        layerKeyframes,
        selectedKeyframe: null,
      });

      const layerMeta = timeline.layers.find((l) => l.id === layerIdStr);
      const isPrimary = layerMeta?.objectId === objectId;

      const patchJson =
        kind === 'drag'
          ? sceneObjectDragPositionPatchJson(obj)
          : sceneObjectTransformPatchJson(obj);

      const persistRemote = async (keyFrameId: number) => {
        if (!movie.currentMovieResponse || !movie.currentScene) return;
        const sceneObjectId = Number.parseInt(objectId, 10);
        if (!Number.isFinite(sceneObjectId)) return;
        try {
          if (signalRStatus !== 'Connected') {
            await connect();
          }
          await sceneObjectPatchStateProperties({
            sceneObjectId,
            keyFrameId,
            patch: patchJson,
          });
        } catch (e) {
          console.error('[Stage] sceneObjectPatchStateProperties after transform failed', e);
        }
      };

      if (target.kind === 'keyframe') {
        const kf = target.keyframe;
        const baseState: KeyframeState =
          kf.objectStates?.[objectId] ??
          (isPrimary
            ? kf.state
            : {
                x: obj.x,
                y: obj.y,
                width: obj.width,
                height: obj.height,
                rotation: obj.rotation,
                opacity: obj.opacity,
              });
        const nextState: KeyframeState =
          kind === 'drag'
            ? { ...baseState, x: obj.x, y: obj.y }
            : kind === 'rotate'
              ? { ...baseState, rotation: obj.rotation }
              : {
                  ...baseState,
                  x: obj.x,
                  y: obj.y,
                  width: obj.width,
                  height: obj.height,
                  rotation: obj.rotation,
                  opacity: obj.opacity,
                };

        if (isPrimary) {
          const objectStates = kf.objectStates
            ? { ...kf.objectStates, [objectId]: nextState }
            : undefined;
          timeline.updateKeyframe(layerIdStr, kf.frame, {
            state: nextState,
            ...(objectStates ? { objectStates } : {}),
          });
        } else {
          timeline.updateKeyframe(layerIdStr, kf.frame, {
            objectStates: { ...(kf.objectStates ?? {}), [objectId]: nextState },
          });
        }

        const kfId = target.keyframeId;
        if (kfId != null) {
          await persistRemote(kfId);
        }
      } else {
        movie.updateSceneObject(objectId, {
          defaultPose: {
            x: obj.x,
            y: obj.y,
            width: obj.width,
            height: obj.height,
            rotation: obj.rotation,
            opacity: obj.opacity,
          },
        });
        await persistRemote(0);
      }
    },
    [connect, sceneObjectPatchStateProperties, signalRStatus]
  );

  // Pan: mousedown/pointerdown ile başlar, mouseup ile biter.
  // Listener'lar senkron eklenir — setState/effect race'i yüzünden click'te takılma olmaz.
  const endPan = useCallback(() => {
    if (panListenersRef.current) {
      window.removeEventListener('pointermove', panListenersRef.current.move);
      window.removeEventListener('pointerup', panListenersRef.current.up);
      window.removeEventListener('pointercancel', panListenersRef.current.up);
      window.removeEventListener('mouseup', panListenersRef.current.up);
      panListenersRef.current = null;
    }
    if (!isPanningRef.current) return;
    isPanningRef.current = false;
    panStartState.current = null;
    wrapperRef.current?.classList.remove('is-panning');
  }, []);

  const startPanAt = useCallback((clientX: number, clientY: number) => {
    if (useEditorStore.getState().currentTool !== 'pan' || !scrollRef.current) return;
    if (isPanningRef.current) return;

    isPanningRef.current = true;
    panStartState.current = {
      scrollX: scrollRef.current.scrollLeft,
      scrollY: scrollRef.current.scrollTop,
      clientX,
      clientY,
    };
    wrapperRef.current?.classList.add('is-panning');

    const handleMove = (e: PointerEvent | MouseEvent) => {
      const scrollEl = scrollRef.current;
      const start = panStartState.current;
      if (!scrollEl || !start) return;
      scrollEl.scrollLeft = start.scrollX + (start.clientX - e.clientX);
      scrollEl.scrollTop = start.scrollY + (start.clientY - e.clientY);
    };

    const handleUp = () => {
      endPan();
    };

    panListenersRef.current = { move: handleMove, up: handleUp };
    window.addEventListener('pointermove', handleMove, { passive: true });
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    window.addEventListener('mouseup', handleUp);
  }, [endPan]);

  useEffect(() => {
    if (currentTool !== 'pan') {
      endPan();
    }
  }, [currentTool, endPan]);

  useEffect(() => () => endPan(), [endPan]);

  const handleObjectPointerDown = useCallback((e: React.PointerEvent, obj: SceneObject) => {
    if (e.button !== 0) return;

    if (currentTool === 'pan') {
      e.preventDefault();
      e.stopPropagation();
      startPanAt(e.clientX, e.clientY);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (!isDragAllowedForTool()) return;
    if (obj.locked || isLayerLocked(obj.layerId)) return;

    if (e.shiftKey) {
      addToSelection(obj.id);
    } else if (!selectedIds.includes(obj.id)) {
      select(obj.id);
    }

    // Ensure we keep receiving move/up events even if pointer leaves the element
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    setDragState({
      isDragging: true,
      objectId: obj.id,
      startX: e.clientX,
      startY: e.clientY,
      objectStartX: obj.x,
      objectStartY: obj.y,
    });
  }, [currentTool, startPanAt, isDragAllowedForTool, isLayerLocked, selectedIds, addToSelection, select]);

  // Fallback: mouse down (non-pointer environments)
  const handleObjectMouseDown = useCallback((e: React.MouseEvent, obj: SceneObject) => {
    if (e.button !== 0) return;

    // Pan pointer path owns drag; mouse fallback only for select/move.
    if (currentTool === 'pan') {
      e.preventDefault();
      e.stopPropagation();
      startPanAt(e.clientX, e.clientY);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (!isDragAllowedForTool()) return;
    if (obj.locked || isLayerLocked(obj.layerId)) return;

    if (e.shiftKey) {
      addToSelection(obj.id);
    } else if (!selectedIds.includes(obj.id)) {
      select(obj.id);
    }

    setDragState({
      isDragging: true,
      objectId: obj.id,
      startX: e.clientX,
      startY: e.clientY,
      objectStartX: obj.x,
      objectStartY: obj.y,
    });
  }, [currentTool, startPanAt, isDragAllowedForTool, isLayerLocked, selectedIds, addToSelection, select]);

  // Handle resize handle pointer down - uses pointer capture for reliability
  const handleResizePointerDown = useCallback((e: React.PointerEvent, handle: ResizeHandle, obj: SceneObject) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    if (currentTool === 'pan') return;
    if (isGroupContainer(obj)) return;
    if (obj.locked || isLayerLocked(obj.layerId)) return;

    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    setResizeState({
      isResizing: true,
      objectId: obj.id,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      objectStartX: obj.x,
      objectStartY: obj.y,
      objectStartWidth: obj.width,
      objectStartHeight: obj.height,
    });
  }, [currentTool, isLayerLocked]);

  const handleRotatePointerDown = useCallback((e: React.PointerEvent, obj: SceneObject) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    if (currentTool === 'pan') return;
    if (!stageRotateEnabled) return;
    if (obj.locked || isLayerLocked(obj.layerId)) return;

    const coords = getStageCoordinates(e);
    if (!coords) return;

    const objectsById = buildObjectsById(currentScene?.objects ?? []);
    const world = getWorldPose(obj, objectsById);
    const pivot = parsePivot(obj.pivotPoint);
    const off = pivotOffsetPx(pivot, obj.width, obj.height);
    const centerX = world.x + off.x;
    const centerY = world.y + off.y;
    const startPointerAngle =
      (Math.atan2(coords.y - centerY, coords.x - centerX) * 180) / Math.PI;

    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    setRotateState({
      isRotating: true,
      objectId: obj.id,
      startPointerAngle,
      objectStartRotation: obj.rotation,
      centerX,
      centerY,
    });
  }, [currentTool, stageRotateEnabled, isLayerLocked, getStageCoordinates, currentScene?.objects]);

  const handlePivotPointerDown = useCallback((e: React.PointerEvent, obj: SceneObject) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    if (currentTool === 'pan') return;
    if (!stageRotateEnabled) return;
    if (obj.locked || isLayerLocked(obj.layerId)) return;

    const pivot = parsePivot(obj.pivotPoint);

    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    setPivotDragState({
      isDragging: true,
      objectId: obj.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startOx: pivot.ox,
      startOy: pivot.oy,
      startX: obj.x,
      startY: obj.y,
      width: obj.width,
      height: obj.height,
      rotation: obj.rotation,
    });
  }, [currentTool, stageRotateEnabled, isLayerLocked]);

  const persistPivot = useCallback(
    async (objectId: string, pivotPoint: string) => {
      const sceneObjectId = Number.parseInt(objectId, 10);
      if (!Number.isFinite(sceneObjectId)) return;
      try {
        if (signalRStatus !== 'Connected') await connect();
        await sceneObjectUpdatePivot({ id: sceneObjectId, pivotPoint });
      } catch (err) {
        console.error('[Stage] sceneObjectUpdatePivot failed', err);
      }
    },
    [connect, sceneObjectUpdatePivot, signalRStatus]
  );

  // Handle mouse move (drag, resize, rotate, or drawing)
  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (dragState.isDragging && dragState.objectId) {
        const dx = (e.clientX - dragState.startX) / zoom;
        const dy = (e.clientY - dragState.startY) / zoom;

        const objects = currentScene?.objects ?? [];
        const objectsById = buildObjectsById(objects);
        const dragged = objectsById.get(dragState.objectId);
        const parent = dragged?.parentObjectId
          ? objectsById.get(dragged.parentObjectId) ?? null
          : null;
        const parentWorldRot = parent ? getWorldPose(parent, objectsById).rotation : 0;
        const rad = (-parentWorldRot * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const localDx = dx * cos - dy * sin;
        const localDy = dx * sin + dy * cos;

        const newX = snapValue(dragState.objectStartX + localDx);
        const newY = snapValue(dragState.objectStartY + localDy);

        updateSceneObject(dragState.objectId, { x: newX, y: newY });
      }

      if (rotateState.isRotating && rotateState.objectId) {
        const coords = getStageCoordinates(e);
        if (coords) {
          const currentAngle =
            (Math.atan2(coords.y - rotateState.centerY, coords.x - rotateState.centerX) * 180) /
            Math.PI;
          let nextRotation =
            rotateState.objectStartRotation + (currentAngle - rotateState.startPointerAngle);
          if (e.shiftKey) {
            nextRotation = Math.round(nextRotation / 15) * 15;
          }
          updateSceneObject(rotateState.objectId, {
            rotation: normalizeRotationDegrees(nextRotation),
          });
        }
      }

      if (pivotDragState.isDragging && pivotDragState.objectId) {
        // Pointer delta in object-local space (counter-rotate by object rotation).
        const screenDx = (e.clientX - pivotDragState.startClientX) / zoom;
        const screenDy = (e.clientY - pivotDragState.startClientY) / zoom;
        const rad = (-pivotDragState.rotation * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const localDx = screenDx * cos - screenDy * sin;
        const localDy = screenDx * sin + screenDy * cos;

        const w = Math.max(1, pivotDragState.width);
        const h = Math.max(1, pivotDragState.height);
        const nextOx = Math.min(1, Math.max(0, pivotDragState.startOx + localDx / w));
        const nextOy = Math.min(1, Math.max(0, pivotDragState.startOy + localDy / h));
        const from = { ox: pivotDragState.startOx, oy: pivotDragState.startOy };
        const to = { ox: nextOx, oy: nextOy };
        const compensated = compensateTopLeftForPivotChange(
          pivotDragState.startX,
          pivotDragState.startY,
          w,
          h,
          pivotDragState.rotation,
          from,
          to
        );

        updateSceneObject(pivotDragState.objectId, {
          pivotPoint: formatPivot(nextOx, nextOy),
          x: Math.round(compensated.x),
          y: Math.round(compensated.y),
        });
      }

      if (resizeState.isResizing && resizeState.objectId && resizeState.handle) {
        const dx = (e.clientX - resizeState.startX) / zoom;
        const dy = (e.clientY - resizeState.startY) / zoom;

        let newX = resizeState.objectStartX;
        let newY = resizeState.objectStartY;
        let newWidth = resizeState.objectStartWidth;
        let newHeight = resizeState.objectStartHeight;
        const aspectEnabled = keepAspectRatio || e.shiftKey;
        const startW = resizeState.objectStartWidth;
        const startH = resizeState.objectStartHeight;
        const ratio = startH > 0 ? startW / startH : 0;
        const minSize = 20;

        // Calculate new dimensions based on handle
        switch (resizeState.handle) {
          case 'e':
            newWidth = Math.max(minSize, startW + dx);
            break;
          case 'w':
            newWidth = Math.max(minSize, startW - dx);
            newX = resizeState.objectStartX + (startW - newWidth);
            break;
          case 's':
            newHeight = Math.max(minSize, startH + dy);
            break;
          case 'n':
            newHeight = Math.max(minSize, startH - dy);
            newY = resizeState.objectStartY + (startH - newHeight);
            break;
          case 'se':
            newWidth = Math.max(minSize, startW + dx);
            newHeight = Math.max(minSize, startH + dy);
            break;
          case 'sw':
            newWidth = Math.max(minSize, startW - dx);
            newHeight = Math.max(minSize, startH + dy);
            newX = resizeState.objectStartX + (startW - newWidth);
            break;
          case 'ne':
            newWidth = Math.max(minSize, startW + dx);
            newHeight = Math.max(minSize, startH - dy);
            newY = resizeState.objectStartY + (startH - newHeight);
            break;
          case 'nw':
            newWidth = Math.max(minSize, startW - dx);
            newHeight = Math.max(minSize, startH - dy);
            newX = resizeState.objectStartX + (startW - newWidth);
            newY = resizeState.objectStartY + (startH - newHeight);
            break;
        }

        if (aspectEnabled && ratio > 0) {
          const handle = resizeState.handle;
          const isCorner = handle === 'nw' || handle === 'ne' || handle === 'se' || handle === 'sw';
          const isHorizontal = handle === 'e' || handle === 'w';
          const isVertical = handle === 'n' || handle === 's';

          if (isCorner) {
            // Choose dominant axis to feel natural under mouse
            const widthDriven = Math.abs(newWidth - startW) >= Math.abs(newHeight - startH);
            if (widthDriven) {
              newHeight = Math.max(minSize, newWidth / ratio);
            } else {
              newWidth = Math.max(minSize, newHeight * ratio);
            }

            // Re-anchor X/Y depending on which corner is fixed
            if (handle === 'sw' || handle === 'nw') {
              newX = resizeState.objectStartX + (startW - newWidth);
            }
            if (handle === 'ne' || handle === 'nw') {
              newY = resizeState.objectStartY + (startH - newHeight);
            }
          } else if (isHorizontal) {
            // Keep vertical center while adjusting height
            const centerY = resizeState.objectStartY + startH / 2;
            newHeight = Math.max(minSize, newWidth / ratio);
            newY = centerY - newHeight / 2;
          } else if (isVertical) {
            // Keep horizontal center while adjusting width
            const centerX = resizeState.objectStartX + startW / 2;
            newWidth = Math.max(minSize, newHeight * ratio);
            newX = centerX - newWidth / 2;
          }
        }

        // Snap (try to preserve ratio by snapping primary axis first)
        const snappedX = snapValue(newX);
        const snappedY = snapValue(newY);
        let snappedW = snapValue(newWidth);
        let snappedH = snapValue(newHeight);
        if (aspectEnabled && ratio > 0) {
          const handle = resizeState.handle;
          const snapByWidth = handle === 'e' || handle === 'w' || handle === 'ne' || handle === 'nw' || handle === 'se' || handle === 'sw';
          if (snapByWidth) {
            snappedW = Math.max(minSize, snapValue(newWidth));
            snappedH = Math.max(minSize, snapValue(snappedW / ratio));
          } else {
            snappedH = Math.max(minSize, snapValue(newHeight));
            snappedW = Math.max(minSize, snapValue(snappedH * ratio));
          }
        }

        updateSceneObject(resizeState.objectId, {
          x: snappedX,
          y: snappedY,
          width: Math.max(minSize, snappedW),
          height: Math.max(minSize, snappedH),
        });
      }
    };

    const handlePointerUp = () => {
      if (resizeState.isResizing && resizeState.objectId) {
        void commitLayoutAfterTransform(resizeState.objectId, 'resize');
      } else if (rotateState.isRotating && rotateState.objectId) {
        void commitLayoutAfterTransform(rotateState.objectId, 'rotate');
      } else if (dragState.isDragging && dragState.objectId) {
        void commitLayoutAfterTransform(dragState.objectId, 'drag');
      } else if (pivotDragState.isDragging && pivotDragState.objectId) {
        const obj = currentScene?.objects.find((o) => o.id === pivotDragState.objectId);
        if (obj) {
          void persistPivot(obj.id, obj.pivotPoint ?? 'center');
          // Pivot change may also shift x/y — persist layout pose.
          void commitLayoutAfterTransform(obj.id, 'drag');
        }
      }

      setDragState(prev => ({ ...prev, isDragging: false, objectId: null }));
      setResizeState(prev => ({ ...prev, isResizing: false, objectId: null, handle: null }));
      setRotateState(prev => ({ ...prev, isRotating: false, objectId: null }));
      setPivotDragState((prev) => ({ ...prev, isDragging: false, objectId: null }));
    };

    if (
      dragState.isDragging ||
      resizeState.isResizing ||
      rotateState.isRotating ||
      pivotDragState.isDragging
    ) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [
    dragState,
    resizeState,
    pivotDragState,
    rotateState,
    zoom,
    snapValue,
    updateSceneObject,
    keepAspectRatio,
    commitLayoutAfterTransform,
    getStageCoordinates,
    currentScene?.objects,
    persistPivot,
  ]);

  // Handle double click for text editing
  const handleObjectDoubleClick = useCallback((e: React.MouseEvent, obj: SceneObject) => {
    e.stopPropagation();
    if (obj.type === 'text' && !obj.locked && !isLayerLocked(obj.layerId)) {
      setEditingTextId(obj.id);
    }
  }, [isLayerLocked]);

  // Handle text change
  const handleTextChange = useCallback((objectId: string, newText: string) => {
    updateSceneObject(objectId, { name: newText });
  }, [updateSceneObject]);

  // Handle text blur (end editing)
  const handleTextBlur = useCallback(() => {
    setEditingTextId(null);
  }, []);

  const handleStageDragOver = (e: React.DragEvent) => {
    if (!hasLibraryItemDragPayload(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsLibraryItemDragOver(true);
  };

  const handleStageDragLeave = (e: React.DragEvent) => {
    if (!hasLibraryItemDragPayload(e)) return;
    e.preventDefault();
    // Only clear when actually leaving the wrapper (not when entering a child)
    const related = e.relatedTarget as Node | null;
    if (related && wrapperRef.current?.contains(related)) return;
    setIsLibraryItemDragOver(false);
  };

  const libraryItemFromUpload = (uploaded: LibraryObjectUploadResponse, fallbackName: string): LibraryItem => ({
    id: String(uploaded.id),
    name: uploaded.name || fallbackName,
    type: 'image',
    src: uploaded.url ? resolveAssetUrl(uploaded.url) : '',
    thumbnail: uploaded.thumbnailUrl ? resolveAssetUrl(uploaded.thumbnailUrl) : undefined,
  });

  const handleStageDrop = (e: React.DragEvent) => {
    setIsLibraryItemDragOver(false);
    const droppedVector = parseDroppedVectorFile(e);
    if (droppedVector && currentScene && currentMovie && stageRef.current) {
      e.preventDefault();
      e.stopPropagation();

      const rect = stageRef.current.getBoundingClientRect();
      const dropX = (e.clientX - rect.left) / zoom;
      const dropY = (e.clientY - rect.top) / zoom;
      const nativeWidth = droppedVector.width && droppedVector.width > 0 ? droppedVector.width : 100;
      const nativeHeight = droppedVector.height && droppedVector.height > 0 ? droppedVector.height : 100;
      const longestSide = Math.max(nativeWidth, nativeHeight);
      const stageScale = longestSide < 64 ? 96 / longestSide : 1;
      const width = nativeWidth * stageScale;
      const height = nativeHeight * stageScale;
      const nextX = snapValue(dropX - width / 2);
      const nextY = snapValue(dropY - height / 2);

      void importVectorPackageFile(
        droppedVector.packageId!,
        droppedVector.fileId!,
        Number(currentMovie.id),
        accessToken
      )
        .then((uploaded) => {
          const item = libraryItemFromUpload(uploaded, droppedVector.name || 'vector');
          addLibraryItem(item);
          return persistFromLibraryItem(item, nextX, nextY);
        })
        .then((createdObject) => {
          if (!createdObject) return;
          addSceneObject(createdObject);
          select(createdObject.id);
        })
        .catch((error) => {
          console.error('[Stage] Failed to import dropped vector package file:', error);
        });
      return;
    }

    const droppedItem = parseDroppedLibraryItem(e);
    if (!droppedItem || !currentScene || !currentMovie || !stageRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = stageRef.current.getBoundingClientRect();
    const dropX = (e.clientX - rect.left) / zoom;
    const dropY = (e.clientY - rect.top) / zoom;
    const width = 100;
    const height = 100;

    const nextX = snapValue(dropX - width / 2);
    const nextY = snapValue(dropY - height / 2);

    persistFromLibraryItem(droppedItem, nextX, nextY)
      .then((createdObject) => {
        if (!createdObject) return;
        // Some create responses don't include full asset path yet.
        // Reuse dragged library src so object is visible immediately.
        if (!createdObject.src && droppedItem.src) {
          createdObject.src = droppedItem.src;
        }
        addSceneObject(createdObject);
        select(createdObject.id);
      })
      .catch((error) => {
        console.error('[Stage] Failed to persist dropped scene object:', error);
      });
  };

  const persistDeleteSceneObjects = useCallback(
    async (objects: SceneObject[]) => {
      if (!currentMovie || !currentScene || objects.length === 0) return;

      const deletePayload = objects
        .map((obj) => ({
          id: Number(obj.id),
          movieId: Number(currentMovie.id),
          sceneId: Number(currentScene.id),
          layerId: obj.layerId ?? 0,
          libraryObjectId: obj.libraryObjectId ?? null,
          name: obj.name,
          description: '',
          type: obj.type,
          body: '',
          fileName: undefined,
          fullPath: '',
          isVisible: obj.visible,
          isLocked: obj.locked,
          width: obj.width,
          height: obj.height,
          dropPosition: { top: obj.y, left: obj.x },
          defaultState: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }))
        .filter((item) => Number.isFinite(item.id));

      if (deletePayload.length === 0) return;

      try {
        if (signalRStatus !== 'Connected') await connect();
        await sceneObjectDeleteBatch(deletePayload);
        objects.forEach((obj) => deleteSceneObject(obj.id));
        if (objects.some((obj) => selectedIds.includes(obj.id))) {
          clearSelection();
        }
      } catch (error) {
        console.error('[Stage] Failed to delete scene objects:', error);
      }
    },
    [
      currentMovie,
      currentScene,
      signalRStatus,
      connect,
      sceneObjectDeleteBatch,
      deleteSceneObject,
      selectedIds,
      clearSelection,
    ]
  );

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return (
        target.isContentEditable ||
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT'
      );
    };

    const handleDeleteKey = async (e: KeyboardEvent) => {
      if (e.key !== 'Delete') return;
      if (isTypingTarget(e.target)) return;

      const { selectedKeyframe, selectedTween } = useTimelineStore.getState();
      if (selectedKeyframe || selectedTween) return;

      if (!currentScene || selectedIds.length === 0) return;

      const selectedObjects = currentScene.objects.filter((obj) => selectedIds.includes(obj.id));
      if (selectedObjects.length === 0) return;

      e.preventDefault();
      await persistDeleteSceneObjects(selectedObjects);
    };

    window.addEventListener('keydown', handleDeleteKey);
    return () => window.removeEventListener('keydown', handleDeleteKey);
  }, [currentScene, selectedIds, persistDeleteSceneObjects]);

  // Arrow keys: selected objects ±1px (key repeat supported)
  useEffect(() => {
    const NUDGE_BY_KEY: Record<string, { dx: number; dy: number }> = {
      ArrowLeft: { dx: -1, dy: 0 },
      ArrowRight: { dx: 1, dy: 0 },
      ArrowUp: { dx: 0, dy: -1 },
      ArrowDown: { dx: 0, dy: 1 },
    };

    const pendingNudgeIds = new Set<string>();
    let persistTimer: number | null = null;

    const flushNudgePersist = () => {
      persistTimer = null;
      const ids = [...pendingNudgeIds];
      pendingNudgeIds.clear();
      for (const id of ids) {
        void commitLayoutAfterTransform(id, 'drag');
      }
    };

    const scheduleNudgePersist = (objectId: string) => {
      pendingNudgeIds.add(objectId);
      if (persistTimer != null) {
        window.clearTimeout(persistTimer);
      }
      persistTimer = window.setTimeout(flushNudgePersist, 150);
    };

    const handleNudgeKey = (e: KeyboardEvent) => {
      const delta = NUDGE_BY_KEY[e.key];
      if (!delta) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const target = e.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (
          target.isContentEditable ||
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT'
        ) {
          return;
        }
      }

      const movie = useMovieStore.getState();
      const selection = useSelectionStore.getState();
      const timeline = useTimelineStore.getState();
      if (!movie.currentScene || selection.selectedIds.length === 0) return;

      const movable = movie.currentScene.objects.filter((obj) => {
        if (!selection.selectedIds.includes(obj.id)) return false;
        if (obj.locked) return false;
        if (obj.layerId != null) {
          const layer = timeline.layers.find((l) => String(l.id) === String(obj.layerId));
          if (layer?.locked) return false;
        }
        return true;
      });
      if (movable.length === 0) return;

      e.preventDefault();

      for (const obj of movable) {
        const nextX = obj.x + delta.dx;
        const nextY = obj.y + delta.dy;
        updateSceneObject(obj.id, { x: nextX, y: nextY });
        scheduleNudgePersist(obj.id);
      }
    };

    window.addEventListener('keydown', handleNudgeKey);
    return () => {
      window.removeEventListener('keydown', handleNudgeKey);
      if (persistTimer != null) {
        window.clearTimeout(persistTimer);
        flushNudgePersist();
      }
    };
  }, [commitLayoutAfterTransform, updateSceneObject]);

  // Render resize / rotate / pivot handles for selected object
  const renderResizeHandles = useCallback((obj: SceneObject) => {
    if (currentTool === 'pan') return null;
    if (!selectedIds.includes(obj.id) || obj.locked || isLayerLocked(obj.layerId)) return null;

    const handles: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
    const rotateHandles: RotateHandle[] = ['nw', 'ne', 'se', 'sw'];
    const pivot = parsePivot(obj.pivotPoint);

    return (
      <>
        {stageResizeEnabled &&
          !isGroupContainer(obj) &&
          handles.map((handle) => (
            <div
              key={handle}
              className={`resize-handle resize-handle-${handle}`}
              onPointerDown={(e) => handleResizePointerDown(e, handle, obj)}
            />
          ))}
        {stageRotateEnabled &&
          rotateHandles.map((handle) => (
            <div
              key={`rotate-${handle}`}
              className={`rotate-handle rotate-handle-${handle}`}
              title={t('rightSidebar.properties.stageRotate')}
              onPointerDown={(e) => handleRotatePointerDown(e, obj)}
            />
          ))}
        {stageRotateEnabled && (
          <div
            className="pivot-handle"
            title={t('rightSidebar.properties.pivotHint')}
            style={
              {
                '--pivot-left': `${pivot.ox * 100}%`,
                '--pivot-top': `${pivot.oy * 100}%`,
              } as React.CSSProperties
            }
            onPointerDown={(e) => handlePivotPointerDown(e, obj)}
          />
        )}
      </>
    );
  }, [
    currentTool,
    selectedIds,
    isLayerLocked,
    handleResizePointerDown,
    handleRotatePointerDown,
    handlePivotPointerDown,
    stageResizeEnabled,
    stageRotateEnabled,
    t,
  ]);

  /** Same layer + same parent DOM siblings only — does not change layer membership. */
  const moveObjectInStack = useCallback((objectId: string, action: 'front' | 'back' | 'forward' | 'backward') => {
    if (!currentScene) return;

    const objects = currentScene.objects ?? [];
    const target = objects.find((item) => item.id === objectId);
    if (!target) return;

    const parentId = target.parentObjectId ?? null;
    const layerKey = target.layerId != null ? String(target.layerId) : null;

    const siblings = objects
      .filter((item) => {
        const itemParent = item.parentObjectId ?? null;
        const itemLayer = item.layerId != null ? String(item.layerId) : null;
        return itemParent === parentId && itemLayer === layerKey;
      })
      .sort((a, b) => a.zIndex - b.zIndex || a.id.localeCompare(b.id));

    const currentIndex = siblings.findIndex((item) => item.id === objectId);
    if (currentIndex === -1 || siblings.length < 2) return;

    const next = [...siblings];
    const [removed] = next.splice(currentIndex, 1);
    if (!removed) return;

    switch (action) {
      case 'front':
        next.push(removed);
        break;
      case 'back':
        next.unshift(removed);
        break;
      case 'forward':
        next.splice(Math.min(currentIndex + 1, next.length), 0, removed);
        break;
      case 'backward':
        next.splice(Math.max(currentIndex - 1, 0), 0, removed);
        break;
    }

    if (next.every((item, index) => item.id === siblings[index]?.id)) return;

    const updatesById: Record<string, Partial<SceneObject>> = {};
    const resortItems: Array<{ id: number; sorter: number }> = [];
    next.forEach((item, index) => {
      if (item.zIndex !== index) {
        updatesById[item.id] = { zIndex: index };
      }
      const parsedId = Number(item.id);
      if (Number.isFinite(parsedId)) {
        resortItems.push({ id: parsedId, sorter: index });
      }
    });
    if (Object.keys(updatesById).length === 0) return;

    batchUpdateSceneObjects(updatesById);

    if (resortItems.length === 0) return;

    void (async () => {
      try {
        if (signalRStatus !== 'Connected') {
          await connect();
        }
        await sceneObjectsResort({ items: resortItems });
      } catch (error) {
        console.error('[Stage] Failed to persist object stack order:', error);
      }
    })();
  }, [currentScene, batchUpdateSceneObjects, signalRStatus, connect, sceneObjectsResort]);

  const persistObjectVisibility = useCallback(
    async (objectId: string, nextVisible: boolean) => {
      const parsedId = Number(objectId);
      if (!Number.isFinite(parsedId)) return;
      updateSceneObject(objectId, { visible: nextVisible });
      try {
        if (signalRStatus !== 'Connected') {
          await connect();
        }
        const saved = await sceneObjectSetVisibility({ id: parsedId, isVisible: nextVisible });
        updateSceneObject(objectId, { visible: saved.isVisible !== false });
      } catch (error) {
        console.error('[Stage] Failed to persist visibility:', error);
        updateSceneObject(objectId, { visible: !nextVisible });
      }
    },
    [signalRStatus, connect, sceneObjectSetVisibility, updateSceneObject],
  );

  const persistObjectLock = useCallback(
    async (objectId: string, nextLocked: boolean) => {
      const parsedId = Number(objectId);
      if (!Number.isFinite(parsedId)) return;
      updateSceneObject(objectId, { locked: nextLocked });
      try {
        if (signalRStatus !== 'Connected') {
          await connect();
        }
        const saved = await sceneObjectSetLock({ id: parsedId, isLocked: nextLocked });
        updateSceneObject(objectId, { locked: saved.isLocked === true });
      } catch (error) {
        console.error('[Stage] Failed to persist lock:', error);
        updateSceneObject(objectId, { locked: !nextLocked });
      }
    },
    [signalRStatus, connect, sceneObjectSetLock, updateSceneObject],
  );

  const duplicateObject = useCallback((obj: SceneObject) => {
    if (!currentScene) return;

    const maxZ = currentScene.objects.reduce((max, item) => Math.max(max, item.zIndex), 0);
    const duplicated: SceneObject = {
      ...obj,
      id: `obj-${crypto.randomUUID()}`,
      name: `${obj.name} Copy`,
      x: snapValue(obj.x + 20),
      y: snapValue(obj.y + 20),
      zIndex: maxZ + 1,
      locked: false,
    };

    if (obj.libraryObjectId) {
      const libraryItemForDuplicate: LibraryItem = {
        id: String(obj.libraryObjectId),
        name: duplicated.name,
        type: obj.type === 'clip' ? 'clip' : 'image',
        src: obj.src ?? '',
      };

      persistFromLibraryItem(libraryItemForDuplicate, duplicated.x, duplicated.y)
        .then((createdObject) => {
          if (!createdObject) return;
          if (!createdObject.src && libraryItemForDuplicate.src) {
            createdObject.src = libraryItemForDuplicate.src;
          }
          addSceneObject(createdObject);
          select(createdObject.id);
        })
        .catch((error) => {
          console.error('[Stage] Failed to persist duplicated library scene object:', error);
        });
      return;
    }

    persistShapeObject(duplicated)
      .then((createdObject) => {
        if (!createdObject) return;
        addSceneObject(createdObject);
        select(createdObject.id);
      })
      .catch((error) => {
        console.error('[Stage] Failed to persist duplicated scene object:', error);
      });
  }, [currentScene, snapValue, persistFromLibraryItem, persistShapeObject, addSceneObject, select]);

  const handleStagePanPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      if (useEditorStore.getState().currentTool !== 'pan') return;
      e.preventDefault();
      startPanAt(e.clientX, e.clientY);
    },
    [startPanAt],
  );

  // Display layers: use timeline layers, or fallback to one layer per object when empty
  const sortedTimelineLayers = useMemo(() => sortTimelineLayers(layers), [layers]);
  const objectFallbackLayers = useMemo(
    () =>
      (currentScene?.objects ?? []).map((obj, i) => ({
        id: obj.id,
        objectId: obj.id,
        name: obj.name,
        order: i,
        visible: obj.visible,
        locked: obj.locked,
      })),
    [currentScene?.objects],
  );
  const displayLayers = layers.length > 0 ? sortedTimelineLayers : objectFallbackLayers;

  const maxLayerOrder = useMemo(() => {
    if (displayLayers.length === 0) return 0;
    return displayLayers.reduce((max, layer) => Math.max(max, layer.order ?? 0), 0);
  }, [displayLayers]);

  // Objects grouped by layer (single pass, stable refs) - avoids O(layers*objects) filter per render
  const objectsByLayer = useMemo(() => {
    const displayLayerIdSet = new Set(displayLayers.map((l) => l.id));
    const fallbackLayerId = displayLayers.length > 0 ? displayLayers[0]!.id : null;

    const map = new Map<string, SceneObject[]>();
    const objs = currentScene?.objects ?? [];
    for (const obj of objs) {
      let lid = obj.layerId != null ? String(obj.layerId) : obj.id;
      // If an object ends up on a layer that isn't currently in the timeline layer list,
      // still render it by falling back to the first available layer.
      if (!displayLayerIdSet.has(lid) && fallbackLayerId) {
        lid = fallbackLayerId;
      }
      if (!map.has(lid)) map.set(lid, []);
      map.get(lid)!.push(obj);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.zIndex - b.zIndex);
    return map;
  }, [currentScene?.objects, displayLayers]);

  const sendObjectsToLayer = useCallback((objectIds: string[], targetLayerId: number) => {
    const objs = currentScene?.objects ?? [];
    let nextZ = Math.max(
      -1,
      ...objs
        .filter((o) => o.layerId === targetLayerId)
        .map((o) => o.zIndex ?? 0),
    ) + 1;

    for (const objectId of objectIds) {
      updateSceneObject(objectId, { layerId: targetLayerId, zIndex: nextZ++ });
    }
  }, [currentScene?.objects, updateSceneObject]);

  const childrenByParent = useMemo(
    () => buildChildrenByParent(currentScene?.objects ?? []),
    [currentScene?.objects]
  );

  /** Aynı layer’da “Send into” adayları: self + kendi alt ağacı hariç (cycle / zaten child). */
  const getSendIntoCandidates = useCallback(
    (target: SceneObject): SceneObject[] => {
      const objects = currentScene?.objects ?? [];
      if (target.layerId == null) return [];

      const layerId = String(target.layerId);
      const descendantIds = collectDescendantIds(target.id, childrenByParent);

      return objects
        .filter((obj) => {
          if (obj.id === target.id) return false;
          if (obj.layerId == null || String(obj.layerId) !== layerId) return false;
          if (descendantIds.has(obj.id)) return false;
          return true;
        })
        .sort((a, b) => a.name.localeCompare(b.name) || a.zIndex - b.zIndex);
    },
    [currentScene?.objects, childrenByParent]
  );

  const persistReparent = useCallback(
    async (
      objectId: string,
      patch: { parentObjectId: string | null; x: number; y: number; rotation: number },
      layerId: number
    ) => {
      const current = useMovieStore.getState().currentScene?.objects.find((o) => o.id === objectId);
      updateSceneObject(objectId, patch);

      const idNum = Number.parseInt(objectId, 10);
      if (!Number.isFinite(idNum)) return;

      const layoutSource: SceneObject = {
        ...(current ?? ({
          id: objectId,
          type: 'image',
          name: '',
          width: 0,
          height: 0,
          opacity: 1,
          zIndex: 0,
          visible: true,
          locked: false,
        } as SceneObject)),
        ...patch,
      };

      try {
        if (signalRStatus !== 'Connected') await connect();
        await sceneObjectUpdateHierarchy({
          id: idNum,
          layerId,
          parentObjectId: patch.parentObjectId != null ? Number.parseInt(patch.parentObjectId, 10) : null,
        });
        await sceneObjectPatchStateProperties({
          sceneObjectId: idNum,
          keyFrameId: 0,
          patch: sceneObjectTransformPatchJson(layoutSource),
        });
      } catch (error) {
        console.error('[Stage] Failed to persist reparent:', error);
      }
    },
    [signalRStatus, connect, sceneObjectUpdateHierarchy, sceneObjectPatchStateProperties, updateSceneObject]
  );

  const sendObjectIntoParent = useCallback(
    async (objectId: string, parentObjectId: string) => {
      const objects = currentScene?.objects ?? [];
      const obj = objects.find((o) => o.id === objectId);
      const parent = objects.find((o) => o.id === parentObjectId);
      if (!obj || !parent || obj.layerId == null) return;
      if (obj.parentObjectId === parentObjectId) return;

      const patch = reparentLayoutPatch(obj, parent, objects);
      await persistReparent(objectId, patch, obj.layerId);
    },
    [currentScene?.objects, persistReparent]
  );

  const createGroupFromSelection = useCallback(
    async (menuTarget: SceneObject | null) => {
      if (!currentMovie || !currentScene) return;
      const objects = currentScene.objects;
      const selected = objects.filter((o) => selectedIds.includes(o.id));
      if (selected.length === 0 && !menuTarget) return;

      const idsInSelection = new Set(selected.map((o) => o.id));
      // Multi shared menu passes null → always wrap in a new group.
      // Per-object menu with 2+ selected can promote that object as container.
      const useExistingContainer =
        menuTarget != null && selected.length >= 2 && idsInSelection.has(menuTarget.id);

      try {
        if (signalRStatus !== 'Connected') await connect();

        if (useExistingContainer) {
          const container = menuTarget;
          if (container.layerId == null) return;

          updateSceneObject(container.id, { isGroup: true });
          const idNum = Number.parseInt(container.id, 10);
          if (Number.isFinite(idNum)) {
            await sceneObjectSave({
              id: idNum,
              movieId: Number(currentMovie.id),
              sceneId: Number(currentScene.id),
              layerId: container.layerId,
              parentObjectId: container.parentObjectId != null ? Number(container.parentObjectId) : null,
              libraryObjectId: container.libraryObjectId ?? null,
              name: container.name,
              description: '',
              type: container.type,
              body: '',
              isVisible: container.visible,
              isLocked: container.locked,
              isGroup: true,
              width: container.width,
              height: container.height,
              dropPosition: { top: container.y, left: container.x },
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }

          // Snapshot objects with isGroup set for layout math
          const objectsForLayout = objects.map((o) =>
            o.id === container.id ? { ...o, isGroup: true } : o
          );

          for (const child of selected) {
            if (child.id === container.id) continue;
            if (child.layerId == null) continue;
            const patch = reparentLayoutPatch(child, container, objectsForLayout);
            await persistReparent(child.id, patch, child.layerId);
          }

          select(container.id);
          return;
        }

        // New empty group wrapper around selection (or single object)
        const members = selected.length >= 1 ? selected : menuTarget ? [menuTarget] : [];
        if (members.length === 0) return;
        const layerId = members[0]?.layerId;
        if (layerId == null) return;

        const objectsById = buildObjectsById(objects);
        const bbox = getWorldAxisAlignedBounds(members, objectsById);
        const maxZ = objects.reduce((max, item) => Math.max(max, item.zIndex), 0);

        const payload: SceneObjectResponse = {
          id: 0,
          movieId: Number(currentMovie.id),
          sceneId: Number(currentScene.id),
          layerId,
          parentObjectId: null,
          libraryObjectId: null,
          pivotPoint: 'center',
          name: t('stage.groupName'),
          description: '',
          type: 'group',
          body: '',
          fullPath: '',
          isVisible: true,
          isLocked: false,
          isGroup: true,
          width: bbox.width,
          height: bbox.height,
          dropPosition: { top: bbox.y, left: bbox.x },
          defaultState: {
            id: 0,
            keyFrameId: 0,
            sceneObjectId: 0,
            properties: JSON.stringify({
              left: bbox.x,
              top: bbox.y,
              width: bbox.width,
              height: bbox.height,
              rotation: 0,
              opacity: 1,
            }),
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const created = await sceneObjectSave(payload);
        const groupObj = mapSceneObjectResponseToStore(created);
        groupObj.isGroup = true;
        groupObj.zIndex = maxZ + 1;
        groupObj.x = bbox.x;
        groupObj.y = bbox.y;
        groupObj.width = bbox.width;
        groupObj.height = bbox.height;
        groupObj.rotation = 0;
        addSceneObject(groupObj);

        const objectsWithGroup = [...objects, groupObj];
        for (const child of members) {
          if (child.layerId == null) continue;
          const patch = reparentLayoutPatch(child, groupObj, objectsWithGroup);
          await persistReparent(child.id, patch, child.layerId);
        }

        select(groupObj.id);
      } catch (error) {
        console.error('[Stage] createGroupFromSelection failed:', error);
      }
    },
    [
      currentMovie,
      currentScene,
      selectedIds,
      signalRStatus,
      connect,
      updateSceneObject,
      sceneObjectSave,
      persistReparent,
      mapSceneObjectResponseToStore,
      addSceneObject,
      select,
      t,
    ]
  );

  const removeFromGroup = useCallback(
    async (targets: SceneObject[]) => {
      if (!currentScene) return;
      const objects = currentScene.objects;
      const objectsById = buildObjectsById(objects);
      const members = targets.filter((obj) => obj.layerId != null && getGroupParent(obj, objectsById) != null);
      if (members.length === 0) return;

      try {
        if (signalRStatus !== 'Connected') await connect();
        for (const obj of members) {
          if (obj.layerId == null) continue;
          const parent = getGroupParent(obj, objectsById);
          if (!parent) continue;
          const grandparent =
            parent.parentObjectId != null ? objectsById.get(parent.parentObjectId) ?? null : null;
          const patch = reparentLayoutPatch(obj, grandparent, objects);
          await persistReparent(obj.id, patch, obj.layerId);
        }
      } catch (error) {
        console.error('[Stage] removeFromGroup failed:', error);
      }
    },
    [currentScene, signalRStatus, connect, persistReparent]
  );

  const getQuickMenuActions = useCallback((obj: SceneObject): SceneObjectQuickMenuActions | null => {
    if (currentTool === 'pan' || isPlaying) return null;
    // Per-object menu only for single selection; multi uses stage-level menu.
    if (selectedIds.length !== 1) return null;
    if (!selectedIds.includes(obj.id) || isLayerLocked(obj.layerId)) return null;

    const objectsById = buildObjectsById(currentScene?.objects ?? []);
    const insideGroup = getGroupParent(obj, objectsById) != null;

    return {
      mode: 'single',
      locked: obj.locked,
      onDuplicate: () => duplicateObject(obj),
      onCreateGroup: () => {
        void createGroupFromSelection(obj);
      },
      onRemoveFromGroup: insideGroup
        ? () => {
            void removeFromGroup([obj]);
          }
        : undefined,
      onToggleLock: () => {
        void persistObjectLock(obj.id, !obj.locked);
      },
      onDelete: () => {
        void persistDeleteSceneObjects([obj]);
      },
    };
  }, [
    currentTool,
    isPlaying,
    selectedIds,
    currentScene?.objects,
    isLayerLocked,
    duplicateObject,
    createGroupFromSelection,
    removeFromGroup,
    persistObjectLock,
    persistDeleteSceneObjects,
  ]);

  const selectedSceneObjects = useMemo(() => {
    if (!currentScene || selectedIds.length === 0) return [];
    const byId = new Map(currentScene.objects.map((o) => [o.id, o]));
    return selectedIds.map((id) => byId.get(id)).filter((o): o is SceneObject => !!o);
  }, [currentScene, selectedIds]);

  const multiSelectionBounds = useMemo(() => {
    if (selectedSceneObjects.length < 2 || !currentScene) return null;
    return getWorldAxisAlignedBounds(
      selectedSceneObjects,
      buildObjectsById(currentScene.objects)
    );
  }, [selectedSceneObjects, currentScene]);

  const multiQuickMenuActions = useMemo((): SceneObjectQuickMenuActions | null => {
    if (currentTool === 'pan' || isPlaying || stageRotateEnabled) return null;
    if (selectedSceneObjects.length < 2) return null;
    if (selectedSceneObjects.every((o) => o.locked || isLayerLocked(o.layerId))) return null;

    const anyUnlocked = selectedSceneObjects.some((o) => !o.locked);
    const selectionHasGroup = selectedSceneObjects.some(isGroupContainer);
    const objectsById = buildObjectsById(currentScene?.objects ?? []);
    const membersInsideGroup = selectedSceneObjects.filter((o) => getGroupParent(o, objectsById) != null);

    return {
      mode: 'multi',
      locked: !anyUnlocked,
      onCreateGroup: selectionHasGroup
        ? undefined
        : () => {
            void createGroupFromSelection(null);
          },
      onRemoveFromGroup: membersInsideGroup.length > 0
        ? () => {
            void removeFromGroup(membersInsideGroup);
          }
        : undefined,
      onToggleLock: () => {
        const nextLocked = anyUnlocked;
        for (const obj of selectedSceneObjects) {
          if (isLayerLocked(obj.layerId)) continue;
          void persistObjectLock(obj.id, nextLocked);
        }
      },
      onDelete: () => {
        void persistDeleteSceneObjects(selectedSceneObjects);
      },
    };
  }, [
    currentTool,
    isPlaying,
    stageRotateEnabled,
    selectedSceneObjects,
    currentScene?.objects,
    isLayerLocked,
    createGroupFromSelection,
    removeFromGroup,
    persistObjectLock,
    persistDeleteSceneObjects,
  ]);

  const ungroupObject = useCallback(
    async (target: SceneObject) => {
      if (!currentMovie || !currentScene) return;
      const objects = currentScene.objects;
      const children = childrenByParent.get(target.id) ?? [];
      if (children.length === 0 && !isGroupContainer(target)) return;

      const parent =
        target.parentObjectId != null
          ? objects.find((o) => o.id === target.parentObjectId) ?? null
          : null;

      try {
        if (signalRStatus !== 'Connected') await connect();

        for (const child of children) {
          if (child.layerId == null) continue;
          const patch = reparentLayoutPatch(child, parent, objects);
          await persistReparent(child.id, patch, child.layerId);
        }

        if (target.type === 'group') {
          const idNum = Number.parseInt(target.id, 10);
          if (Number.isFinite(idNum) && target.layerId != null) {
            deleteSceneObject(target.id);
            await sceneObjectDeleteBatch([
              {
                id: idNum,
                movieId: Number(currentMovie.id),
                sceneId: Number(currentScene.id),
                layerId: target.layerId,
                name: target.name,
                description: '',
                type: target.type,
                body: '',
                isVisible: target.visible,
                isLocked: target.locked,
                isGroup: true,
                width: target.width,
                height: target.height,
                dropPosition: { top: target.y, left: target.x },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              },
            ]);
          }
          clearSelection();
        } else {
          updateSceneObject(target.id, { isGroup: false });
          const idNum = Number.parseInt(target.id, 10);
          if (Number.isFinite(idNum) && target.layerId != null) {
            await sceneObjectSave({
              id: idNum,
              movieId: Number(currentMovie.id),
              sceneId: Number(currentScene.id),
              layerId: target.layerId,
              parentObjectId: target.parentObjectId != null ? Number(target.parentObjectId) : null,
              libraryObjectId: target.libraryObjectId ?? null,
              name: target.name,
              description: '',
              type: target.type,
              body: '',
              isVisible: target.visible,
              isLocked: target.locked,
              isGroup: false,
              width: target.width,
              height: target.height,
              dropPosition: { top: target.y, left: target.x },
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }
        }
      } catch (error) {
        console.error('[Stage] ungroupObject failed:', error);
      }
    },
    [
      currentMovie,
      currentScene,
      childrenByParent,
      signalRStatus,
      connect,
      persistReparent,
      deleteSceneObject,
      sceneObjectDeleteBatch,
      clearSelection,
      updateSceneObject,
      sceneObjectSave,
    ]
  );

  const persistObjectsToLayer = useCallback(async (objectIds: string[], targetLayerId: number) => {
    if (!currentMovie || !currentScene) return;

    if (signalRStatus !== 'Connected') {
      await connect();
    }

    const nowIso = new Date().toISOString();
    const sceneResponse: SceneResponse = {
      id: Number(currentScene.id),
      movieId: Number(currentMovie.id),
      name: currentScene.name,
      description: '',
      framesTotal: 0,
      sorter: 0,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    let serverObjects: SceneObjectResponse[] = [];
    try {
      serverObjects = await sceneObjectsGet(sceneResponse);
    } catch (error) {
      console.error('[Stage] Failed to fetch scene objects for persist:', error);
      return;
    }

    const idSet = new Set(objectIds.map((id) => Number(id)).filter((n) => Number.isFinite(n)));
    const payload: SceneObjectResponse[] = serverObjects
      .filter((o) => idSet.has(o.id))
      .map((o) => ({ ...o, layerId: targetLayerId, updatedAt: nowIso }));

    if (payload.length === 0) return;

    try {
      await sceneObjectsSave(payload);
    } catch (error) {
      console.error('[Stage] Failed to persist send-to-layer:', error);
    }
  }, [currentMovie, currentScene, signalRStatus, connect, sceneObjectsGet, sceneObjectsSave]);

  // Memoized recursive render — children nest inside parent DOM for transform inheritance
  const renderSceneObject = useCallback((obj: SceneObject) => {
    const isVisible = obj.visible && isLayerVisible(obj.layerId);
    const isLocked = obj.locked || isLayerLocked(obj.layerId);
    const isSelected = selectedIds.includes(obj.id);
    const isHovered = hoveredId === obj.id;
    const isEditing = editingTextId === obj.id;
    const childObjects = childrenByParent.get(obj.id) ?? [];
    const quickMenuActions = getQuickMenuActions(obj);

    if (!isVisible) return null;

    return (
      <SceneObjectComponent
        obj={obj}
        isSelected={isSelected}
        isHovered={isHovered}
        isLocked={isLocked}
        isVisible={isVisible}
        isEditing={isEditing}
        onMouseDown={handleObjectMouseDown}
        onPointerDown={handleObjectPointerDown}
        onDoubleClick={handleObjectDoubleClick}
        onContextMenu={(e) => {
          e.stopPropagation();
          if (!selectedIds.includes(obj.id)) select(obj.id);
        }}
        onMouseEnter={() => {
          if (currentTool === 'select') setHovered(obj.id);
        }}
        onMouseLeave={() => setHovered(null)}
        onTextChange={handleTextChange}
        onTextBlur={handleTextBlur}
        renderResizeHandles={renderResizeHandles}
        showQuickMenu={quickMenuActions != null && !stageRotateEnabled}
        quickMenuActions={quickMenuActions}
        renderContextMenu={(targetObj) => {
          const hasChildren = (childrenByParent.get(targetObj.id) ?? []).length > 0;
          const canUngroup = isGroupContainer(targetObj) || hasChildren;
          return (
            <>
              <ContextMenu.Item onSelect={() => duplicateObject(targetObj)}>{t('stage.duplicate')}</ContextMenu.Item>
              <ContextMenu.Item
                onSelect={() => {
                  void createGroupFromSelection(targetObj);
                }}
              >
                {t('stage.createGroup')}
              </ContextMenu.Item>
              <ContextMenu.Item
                disabled={!canUngroup}
                onSelect={() => {
                  void ungroupObject(targetObj);
                }}
              >
                {t('stage.ungroup')}
              </ContextMenu.Item>
              <ContextMenu.Item
                onSelect={() => {
                  void persistObjectLock(targetObj.id, !targetObj.locked);
                }}
              >
                {targetObj.locked ? t('common.unlock') : t('common.lock')}
              </ContextMenu.Item>
              <ContextMenu.Item
                onSelect={() => {
                  void persistObjectVisibility(targetObj.id, !targetObj.visible);
                }}
              >
                {targetObj.visible ? t('common.hide') : t('common.show')}
              </ContextMenu.Item>
              <ContextMenu.Separator />
              <ContextMenu.Label>{t('stage.insideThisLayer')}</ContextMenu.Label>
              <ContextMenu.Item onSelect={() => moveObjectInStack(targetObj.id, 'front')}>{t('stage.bringToFront')}</ContextMenu.Item>
              <ContextMenu.Item onSelect={() => moveObjectInStack(targetObj.id, 'forward')}>{t('stage.bringForward')}</ContextMenu.Item>
              <ContextMenu.Item onSelect={() => moveObjectInStack(targetObj.id, 'backward')}>{t('stage.sendBackward')}</ContextMenu.Item>
              <ContextMenu.Item onSelect={() => moveObjectInStack(targetObj.id, 'back')}>{t('stage.sendToBack')}</ContextMenu.Item>
              <ContextMenu.Separator />
              <ContextMenu.Sub>
                <ContextMenu.SubTrigger>{t('stage.sendToLayer')}</ContextMenu.SubTrigger>
                <ContextMenu.SubContent>
                  {displayLayers.length === 0 ? (
                    <ContextMenu.Item disabled>{t('stage.noLayers')}</ContextMenu.Item>
                  ) : (
                    displayLayers.map((layer) => {
                      const layerIdNum = parseInt(layer.id, 10);
                      const isCurrent = targetObj.layerId != null && String(targetObj.layerId) === String(layer.id);
                      const disabled = Number.isNaN(layerIdNum) || isCurrent;
                      return (
                        <ContextMenu.Item
                          key={layer.id}
                          disabled={disabled}
                          onSelect={() => {
                            if (Number.isNaN(layerIdNum)) return;
                            const idsToMove = selectedIds.includes(targetObj.id) ? selectedIds : [targetObj.id];
                            sendObjectsToLayer(idsToMove, layerIdNum);
                            void persistObjectsToLayer(idsToMove, layerIdNum);
                          }}
                        >
                          {layer.name ?? t('stage.layerFallback', { id: layer.id })}
                        </ContextMenu.Item>
                      );
                    })
                  )}
                </ContextMenu.SubContent>
              </ContextMenu.Sub>
              <ContextMenu.Sub>
                <ContextMenu.SubTrigger>{t('stage.sendInto')}</ContextMenu.SubTrigger>
                <ContextMenu.SubContent>
                  {(() => {
                    const candidates = getSendIntoCandidates(targetObj);
                    if (candidates.length === 0) {
                      return (
                        <ContextMenu.Item disabled>{t('stage.noSendIntoTargets')}</ContextMenu.Item>
                      );
                    }
                    return candidates.map((candidate) => {
                      const isCurrentParent = targetObj.parentObjectId === candidate.id;
                      return (
                        <ContextMenu.Item
                          key={candidate.id}
                          disabled={isCurrentParent}
                          onSelect={() => {
                            void sendObjectIntoParent(targetObj.id, candidate.id);
                          }}
                        >
                          {candidate.name || t('stage.objectFallback', { id: candidate.id })}
                        </ContextMenu.Item>
                      );
                    });
                  })()}
                </ContextMenu.SubContent>
              </ContextMenu.Sub>
              <ContextMenu.Separator />
              <ContextMenu.Item
                color="red"
                onSelect={() => {
                  void persistDeleteSceneObjects([targetObj]);
                }}
              >
                {t('common.delete')}
              </ContextMenu.Item>
            </>
          );
        }}
      >
        {childObjects.map((child) => (
          <Fragment key={child.id}>{renderSceneObject(child)}</Fragment>
        ))}
      </SceneObjectComponent>
    );
  }, [
    isLayerVisible,
    isLayerLocked,
    currentTool,
    selectedIds,
    hoveredId,
    editingTextId,
    childrenByParent,
    handleObjectMouseDown,
    handleObjectPointerDown,
    handleObjectDoubleClick,
    handleTextChange,
    handleTextBlur,
    renderResizeHandles,
    getQuickMenuActions,
    stageRotateEnabled,
    select,
    setHovered,
    duplicateObject,
    createGroupFromSelection,
    ungroupObject,
    persistObjectLock,
    persistObjectVisibility,
    moveObjectInStack,
    sendObjectsToLayer,
    persistObjectsToLayer,
    getSendIntoCandidates,
    sendObjectIntoParent,
    persistDeleteSceneObjects,
    displayLayers,
    t,
  ]);

  return (
    <>
      <ConfirmModal
        open={layerSelectionWarningOpen}
        onOpenChange={setLayerSelectionWarningOpen}
        title={t('stage.noLayerTitle')}
        description={t('stage.noLayerDescription')}
        confirmText={t('common.ok')}
        cancelText={t('common.close')}
        onConfirm={() => setLayerSelectionWarningOpen(false)}
      />
      <div
        ref={wrapperRef}
        className={`stage-wrapper tool-${currentTool} ${isLibraryItemDragOver ? 'library-item-drag-over' : ''}`}
        onPointerDown={(e) => {
          handleStageBackgroundPointerDown(e);
          if (currentTool === 'pan') {
            handleStagePanPointerDown(e);
            return;
          }
          handleStageDrawingMouseDown(e);
        }}
        onDragOver={handleStageDragOver}
        onDragLeave={handleStageDragLeave}
      >
      {/* Library item drop zone: sadece drag/drop, mouseup'tan bagimsiz */}
      <div
        className={`stage-library-drop-zone ${isLibraryItemDragOver ? 'active' : ''}`}
        onDragOver={handleStageDragOver}
        onDragLeave={handleStageDragLeave}
        onDrop={handleStageDrop}
      />
      <StageRulerFrame ref={scrollRef} zoom={zoom}>
        <div
          ref={stageRef}
          className={`stage ${showGrid ? 'show-grid' : ''} tool-${currentTool} ${isPlaying ? 'playmode' : ''}`}
          style={{
            '--stage-width': `${stageWidth}px`,
            '--stage-height': `${stageHeight}px`,
            '--zoom': zoom,
            '--grid-size': `${gridSize}px`,
          } as React.CSSProperties}
          onPointerDown={(e) => {
            if (e.target !== stageRef.current) return;
            handleStagePanPointerDown(e);
          }}
        >
          {displayLayers.map((layer) => {
            const layerObjects = objectsByLayer.get(layer.id) ?? [];
            const layerZIndex = maxLayerOrder - (layer.order ?? 0);
            return (
              <StageLayer
                key={'sl-' + layer.id}
                layer={layer}
                zIndex={layerZIndex}
                objects={layerObjects}
                renderObject={renderSceneObject}
                selectionKey={selectedIds.join(',')}
              />
            );
          })}

          <canvas
            ref={canvasRef}
            className="stage-fixed-canvas"
            width={stageWidth}
            height={stageHeight}
            aria-hidden
          />

          {drawingPreview}

          {marqueeVisual && (
            <div
              className={`stage-marquee stage-marquee--${marqueeVisual.mode}`}
              style={{
                left: marqueeVisual.rect.x,
                top: marqueeVisual.rect.y,
                width: marqueeVisual.rect.width,
                height: marqueeVisual.rect.height,
              }}
              aria-hidden
            />
          )}

          {multiSelectionBounds && multiQuickMenuActions && (
            <SceneObjectQuickMenu
              {...multiQuickMenuActions}
              style={{
                left: multiSelectionBounds.x + multiSelectionBounds.width / 2,
                top: multiSelectionBounds.y - 72,
              }}
            />
          )}
        </div>
      </StageRulerFrame>
      </div>
    </>
  );
}

